import React from 'react';
import { Link } from 'react-router-dom';
import LegalPageNavbar from './LegalPageNavbar';
import './LandingPage.css';

const SYLLERA_TAGLINE =
  'Master Languages Vocabulary by Educational Level with AI-powered learning';

const TermsOfServicePage = () => (
  <div className="landing-page">
    <LegalPageNavbar />

    <section className="section-container" style={{ paddingTop: '96px', paddingBottom: '72px' }}>
      <div className="section-content legal-document" style={{ maxWidth: 900 }}>
        <h1>Terms of Service</h1>
        <p><strong>Last updated:</strong> June 16, 2026</p>
        <p>
          Welcome to <strong>Syllera</strong> ({SYLLERA_TAGLINE}). These Terms of Service (&quot;Terms&quot;)
          govern your access to and use of the Syllera website, mobile applications, and related services
          (collectively, the &quot;Platform&quot;).
        </p>
        <p>
          By creating an account, accessing, or using Syllera, you agree to these Terms and our{' '}
          <Link to="/privacy-policy">Privacy Policy</Link>. If you do not agree, you may not use the Platform.
        </p>

        <h2>1. Introduction</h2>
        <p>
          Syllera (&quot;Syllera,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) provides personalized
          language learning tools for students, teachers, parents, schools, and partners.
        </p>
        <p>
          These Terms form a binding agreement between you and Syllera. If you use the Platform on behalf of a
          school or organization, you represent that you have authority to bind that organization to these Terms.
        </p>

        <h2>2. User Accounts</h2>
        <p>To access certain features, you must create an account. You agree to:</p>
        <ul>
          <li>Provide accurate, current, and complete registration information</li>
          <li>Maintain the security and confidentiality of your login credentials</li>
          <li>Notify us promptly of any unauthorized access to your account</li>
          <li>Accept responsibility for all activity that occurs under your account</li>
        </ul>
        <p>
          You may not share your account with others, impersonate another person, or create accounts using false
          or misleading information. We may suspend or terminate accounts that violate these requirements.
        </p>

        <h2>3. Acceptable Use</h2>
        <p>You agree to use Syllera only for lawful educational purposes. You must not:</p>
        <ul>
          <li>Use the Platform to cheat, plagiarize, or misrepresent academic work</li>
          <li>Abuse, manipulate, or attempt to reverse-engineer AI-powered features</li>
          <li>Upload harmful, offensive, illegal, or infringing content</li>
          <li>Harass, threaten, or harm other users</li>
          <li>Attempt to gain unauthorized access to systems, accounts, or data</li>
          <li>Use automated scripts, bots, or scrapers without our written permission</li>
          <li>Interfere with the proper functioning or security of the Platform</li>
        </ul>
        <p>
          We reserve the right to investigate violations and take appropriate action, including content removal,
          account suspension, or permanent termination.
        </p>

        <h2>4. User-Generated Content</h2>
        <p>
          Users may submit content through the Platform, including messages, assignments, speaking practice
          recordings, writing submissions, and feedback. You retain ownership of content you submit, but grant
          Syllera a limited license to use, store, and process that content solely to operate and improve the
          Platform.
        </p>
        <p>
          You are solely responsible for content you submit. To the maximum extent permitted by law, Syllera is
          not responsible for user-generated content and does not endorse views expressed by users. We may
          remove content that violates these Terms or applicable law.
        </p>

        <h2>5. Educational Content Usage</h2>
        <p>
          Syllera provides vocabulary lessons, quizzes, articles, courses, and learning materials for educational
          use. Unless otherwise stated or licensed:
        </p>
        <ul>
          <li>Content is provided for personal or classroom learning purposes only</li>
          <li>Students should use materials to support genuine learning, not to circumvent academic integrity rules</li>
          <li>Teachers and schools may use Syllera within the scope of their subscription or agreement with us</li>
          <li>You may not copy, redistribute, resell, or publicly republish Platform content without permission</li>
        </ul>

        <h2>6. AI-Powered Features Disclaimer</h2>
        <p>
          Syllera uses artificial intelligence to personalize learning, generate explanations, recommend vocabulary,
          and improve study outcomes. While we strive for accuracy and educational value:
        </p>
        <ul>
          <li>
            AI-generated content is provided for educational support purposes only and may contain inaccuracies.
            Users should independently verify important information.
          </li>
          <li>AI outputs are learning aids and should not be treated as professional, legal, or official advice</li>
          <li>You are responsible for verifying important information and using critical judgment</li>
          <li>We continuously improve our systems but do not guarantee error-free AI responses</li>
        </ul>
        <p>
          Syllera does not guarantee any academic results, exam scores, grades, or learning outcomes. Teachers,
          parents, and students should review AI-assisted work as part of the learning process, especially for
          graded or formal assessments.
        </p>

        <h2>7. Subscriptions and Payments</h2>
        <p>
          Some features require a paid subscription. By purchasing a plan, you agree to the pricing, billing cycle,
          and payment terms presented at checkout. Subscriptions may renew automatically unless canceled before the
          renewal date. Refunds are handled according to our <Link to="/refund-policy">Refund Policy</Link> and
          applicable law.
        </p>

        <h2>8. Intellectual Property</h2>
        <p>
          The Platform, including its software, design, branding, text, graphics, audio, video, curricula,
          vocabulary databases, and AI models, is owned by Syllera or its licensors and is protected by intellectual
          property laws.
        </p>
        <p>
          We grant you a limited, non-exclusive, non-transferable license to access and use the Platform for
          personal or authorized educational purposes in accordance with these Terms. You may not copy, modify,
          distribute, sell, or create derivative works from Syllera content except as expressly permitted.
        </p>

        <h2>9. Learning Progress and Data</h2>
        <p>
          Syllera tracks learning progress, quiz performance, and usage data to personalize your experience and
          provide reports to authorized teachers, parents, or schools. Our collection and use of personal
          information is described in our <Link to="/privacy-policy">Privacy Policy</Link>.
        </p>

        <h2>10. Termination of Accounts</h2>
        <p>
          You may stop using Syllera at any time and may request account deletion through support or account
          settings where available.
        </p>
        <p>We may suspend or terminate your access if you:</p>
        <ul>
          <li>Violate these Terms or applicable laws</li>
          <li>Misuse AI features or educational content</li>
          <li>Engage in fraud, abuse, or harmful conduct</li>
          <li>Fail to pay applicable subscription fees</li>
        </ul>
        <p>
          Upon termination, your right to use the Platform ends immediately. Provisions that by their nature
          should survive termination (including intellectual property, disclaimers, and limitations of liability)
          will remain in effect.
        </p>

        <h2>11. Disclaimer of Warranties</h2>
        <p>
          The Platform is provided on an &quot;as is&quot; and &quot;as available&quot; basis, without warranties
          of any kind, to the fullest extent permitted by law. Syllera disclaims all warranties, express or implied,
          including warranties of merchantability, fitness for a particular purpose, and non-infringement.
        </p>
        <p>
          We do not guarantee uninterrupted, timely, secure, or error-free operation of the Platform. We do not
          guarantee specific learning outcomes, exam results, grades, or academic success. Educational progress
          depends on many factors beyond our control.
        </p>

        <h2>12. Limitation of Liability</h2>
        <p>
          To the maximum extent permitted by law, Syllera and its officers, directors, employees, and partners
          shall not be held liable for any indirect, incidental, special, consequential, or punitive damages,
          including loss of profits, data, goodwill, or learning opportunities, arising from:
        </p>
        <ul>
          <li>Your use of or inability to use the Platform</li>
          <li>Errors or inaccuracies in content or AI-generated responses</li>
          <li>Unauthorized access to your account or data</li>
          <li>Conduct of other users, user-generated content, or third-party services</li>
        </ul>
        <p>
          Our total liability for any claim related to the Platform shall not exceed the amount you paid to
          Syllera in the twelve (12) months preceding the claim, or one hundred U.S. dollars (USD $100) if you
          have not made any payments.
        </p>

        <h2>13. Changes to These Terms</h2>
        <p>
          We may update these Terms from time to time. When we make material changes, we will post the updated
          Terms on this page and update the &quot;Last updated&quot; date. Continued use of the Platform after
          changes take effect constitutes acceptance of the revised Terms.
        </p>

        <h2>14. Governing Law</h2>
        <p>
          These Terms shall be governed by and interpreted in accordance with the laws of the People&apos;s
          Democratic Republic of Algeria, without regard to conflict-of-law principles. Any disputes arising from
          these Terms or your use of the Platform shall be subject to the exclusive jurisdiction of the competent
          courts in Algeria, unless otherwise required by applicable consumer protection laws in your country of
          residence.
        </p>

        <h2>15. Contact Us</h2>
        <p>For questions about these Terms, legal inquiries, or account support, please contact:</p>
        <ul>
          <li>Email: <a href="mailto:support@syllera.com">support@syllera.com</a></li>
          <li>Legal: <a href="mailto:legal@syllera.com">legal@syllera.com</a></li>
          <li>Website: <Link to="/contact">syllera.com/contact</Link></li>
        </ul>
        <p>
          Related policies: <Link to="/privacy-policy">Privacy Policy</Link> ·{' '}
          <Link to="/cookie-policy">Cookie Policy</Link> · <Link to="/refund-policy">Refund Policy</Link>
        </p>
      </div>
    </section>
  </div>
);

export default TermsOfServicePage;
